
import React from 'react';
import { Save, Clock, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

interface AutosaveIndicatorProps {
  hasUnsavedChanges: boolean;
  isAutosaveEnabled: boolean;
  onManualSave: () => void;
  className?: string;
}

export const AutosaveIndicator: React.FC<AutosaveIndicatorProps> = ({
  hasUnsavedChanges,
  isAutosaveEnabled,
  onManualSave,
  className
}) => {
  return (
    <div className={cn("flex items-center gap-2", className)}>
      {/* Autosave status badge */}
      <Badge 
        variant={hasUnsavedChanges ? "secondary" : "default"}
        className={cn(
          "flex items-center gap-1 text-xs",
          hasUnsavedChanges && "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200",
          !hasUnsavedChanges && "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
        )}
      >
        {hasUnsavedChanges ? (
          <>
            <Clock className="w-3 h-3" />
            Unsaved changes
          </>
        ) : (
          <>
            <CheckCircle className="w-3 h-3" />
            All saved
          </>
        )}
      </Badge>

      {/* Manual save button */}
      <Button
        variant="ghost"
        size="sm"
        onClick={onManualSave}
        disabled={!hasUnsavedChanges}
        className="h-7 px-2"
        title="Save manually"
      >
        <Save className="w-3 h-3" />
      </Button>

      {/* Autosave enabled indicator */}
      {isAutosaveEnabled && (
        <Badge variant="outline" className="text-xs">
          Auto-save ON
        </Badge>
      )}
    </div>
  );
};
